import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FinanceTabs, FinanceHeader, money } from "./financeShell";

const BUCKETS = [
  ["current", "Current", "text-emerald-600"],
  ["1_30", "1–30 Days", "text-amber-600"],
  ["31_60", "31–60 Days", "text-orange-600"],
  ["61_90", "61–90 Days", "text-rose-600"],
  ["90_plus", "90+ Days", "text-red-700"],
];

export default function AccountsReceivable() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/finance/accounts-receivable").then((r) => setData(r.data)).catch(() => setData({ aging: {}, total_ar: 0, rows: [] })); }, []);

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5" data-testid="finance-receivables">
      <FinanceHeader title="Accounts Receivable" subtitle="Outstanding balances by aging bucket." />
      <FinanceTabs user={user} />

      {!data ? <div className="py-20 text-center"><Icons.Loader2 className="h-6 w-6 animate-spin text-[#F5A623] inline" /></div> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="ar-buckets">
            <div className="bg-[#171B2E] text-white rounded-xl shadow-sm p-4">
              <div className="text-xs text-slate-300">Total A/R</div>
              <div className="text-xl font-bold mt-1.5">{money(data.total_ar)}</div>
            </div>
            {BUCKETS.map(([k, l, tone]) => (
              <div key={k} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                <div className="text-xs text-slate-500">{l}</div>
                <div className={`text-xl font-bold mt-1.5 ${tone}`}>{money(data.aging[k])}</div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap" data-testid="ar-table">
              <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                {["Invoice #", "Client", "Balance", "Due Date", "Bucket", "Status", ""].map((h) => <th key={h} className="px-3 py-2.5 font-medium">{h}</th>)}
              </tr></thead>
              <tbody>
                {data.rows.length === 0 ? <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-400">No outstanding receivables. 🎉</td></tr> :
                data.rows.map((r) => (
                  <tr key={r.invoice_id} onClick={() => navigate(`/finance/invoices/${r.invoice_id}`)} data-testid={`ar-row-${r.invoice_id}`} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer">
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-700">{r.invoice_number}</td>
                    <td className="px-3 py-2.5 font-medium text-slate-800">{r.client_name || "—"}</td>
                    <td className="px-3 py-2.5 font-semibold text-slate-900">{money(r.balance, r.currency)}</td>
                    <td className="px-3 py-2.5 text-slate-500">{r.due_date ? r.due_date.slice(0, 10) : "—"}</td>
                    <td className="px-3 py-2.5"><span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{(BUCKETS.find((b) => b[0] === r.bucket) || ["", r.bucket])[1]}</span></td>
                    <td className="px-3 py-2.5 capitalize text-slate-500">{r.status.replace(/_/g, " ")}</td>
                    <td className="px-3 py-2.5 text-right"><Icons.ChevronRight className="h-4 w-4 text-slate-300 inline" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
